/**
 * Spatialisation: HRTF panning, occlusion, propagation delay and Doppler.
 *
 * Each spatial voice is:
 *
 *   content ─► input ─► occlusion LP ─► [delay] ─► panner(HRTF) ─► bus
 *                                  └─► send gain ─────────────────► zone reverb
 *
 * The delay line does double duty: it is the propagation delay (a shot 200 m away
 * arrives 0.58 s after its muzzle flash) and, when ramped, a physically correct
 * Doppler shift for fast-moving sources.
 *
 * Occlusion raycasts from the listener to the source through ctx.physics and
 * low-passes by the number and thickness of obstructions. If physics is a stub the
 * voice is simply unoccluded.
 */
import * as THREE from 'three';
import { gain, bq } from './synth/nodes.js';

const SPEED_OF_SOUND = 343;
const MAX_DELAY = 0.9;

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _up = new THREE.Vector3();

function toVec(p, out) {
  if (!p) return null;
  if (p.isVector3) return out.copy(p);
  if (Array.isArray(p)) return out.set(p[0] || 0, p[1] || 0, p[2] || 0);
  if (typeof p.x === 'number') return out.set(p.x, p.y || 0, p.z || 0);
  return null;
}

export class Spatial {
  /**
   * @param {BaseAudioContext} ac
   * @param {import('./Buses.js').Buses} buses
   * @param {import('./Zones.js').Zones} zones
   */
  constructor(ac, buses, zones, { maxVoices = 44 } = {}) {
    this.ac = ac;
    this.buses = buses;
    this.zones = zones;
    this.maxVoices = maxVoices;
    this.active = [];
    this.listener = new THREE.Vector3();
    this.listenerVel = new THREE.Vector3();
    this.listenerQuat = new THREE.Quaternion();
    this.occlusionEnabled = true;
    this.ctx = null;
    this.stats = { spawned: 0, dropped: 0, occluded: 0 };
    this._hrtfBudget = 12;
  }

  attach(ctx) { this.ctx = ctx; }

  /** Number of voices currently sounding. */
  get voiceCount() { return this.active.length; }

  /** True when there is headroom for a voice of this priority. */
  canPlay(priority = 0.5) {
    if (this.active.length < this.maxVoices * 0.7) return true;
    if (this.active.length >= this.maxVoices) return priority >= 0.95;
    return priority >= 0.35;
  }

  /**
   * Update the AudioListener from the camera. Called every frame.
   * @param {THREE.Vector3|number[]} position
   * @param {THREE.Quaternion|number[]} quaternion
   * @param {THREE.Vector3|number[]} velocity
   */
  setListener(position, quaternion, velocity) {
    const l = this.ac.listener;
    const t = this.ac.currentTime;
    const p = toVec(position, _a);
    if (p) {
      this.listener.copy(p);
      if (l.positionX) {
        l.positionX.setTargetAtTime(p.x, t, 0.012);
        l.positionY.setTargetAtTime(p.y, t, 0.012);
        l.positionZ.setTargetAtTime(p.z, t, 0.012);
      } else if (l.setPosition) {
        l.setPosition(p.x, p.y, p.z);
      }
    }
    if (quaternion) {
      const q = quaternion.isQuaternion
        ? this.listenerQuat.copy(quaternion)
        : this.listenerQuat.set(quaternion[0] || 0, quaternion[1] || 0, quaternion[2] || 0, quaternion[3] ?? 1);
      _fwd.set(0, 0, -1).applyQuaternion(q);
      _up.set(0, 1, 0).applyQuaternion(q);
      if (l.forwardX) {
        l.forwardX.setTargetAtTime(_fwd.x, t, 0.012);
        l.forwardY.setTargetAtTime(_fwd.y, t, 0.012);
        l.forwardZ.setTargetAtTime(_fwd.z, t, 0.012);
        l.upX.setTargetAtTime(_up.x, t, 0.012);
        l.upY.setTargetAtTime(_up.y, t, 0.012);
        l.upZ.setTargetAtTime(_up.z, t, 0.012);
      } else if (l.setOrientation) {
        l.setOrientation(_fwd.x, _fwd.y, _fwd.z, _up.x, _up.y, _up.z);
      }
    }
    const v = toVec(velocity, _b);
    if (v) this.listenerVel.copy(v);
  }

  /**
   * Measure obstruction between the listener and a point.
   * @returns {{ count:number, thickness:number, lp:number, atten:number }}
   */
  occlusionAt(point) {
    const out = { count: 0, thickness: 0, lp: 21000, atten: 1 };
    const phys = this.ctx && this.ctx.physics;
    if (!this.occlusionEnabled || !phys || !point) return out;

    _dir.copy(point).sub(this.listener);
    const dist = _dir.length();
    if (dist < 0.6) return out;
    _dir.divideScalar(dist);

    let hits = null;
    try {
      if (typeof phys.raycastAll === 'function') {
        hits = phys.raycastAll(this.listener, _dir, dist - 0.25);
      } else if (typeof phys.raycast === 'function') {
        const h = phys.raycast(this.listener, _dir, dist - 0.25);
        hits = h ? [h] : [];
      }
    } catch {
      return out;
    }
    if (!hits || !hits.length) return out;

    // pairs of surfaces bound a solid: estimate thickness from entry/exit distances
    const ds = hits
      .map((h) => (typeof h.distance === 'number' ? h.distance : (h.point ? this.listener.distanceTo(h.point) : null)))
      .filter((d) => typeof d === 'number' && Number.isFinite(d))
      .sort((a, b) => a - b);
    out.count = ds.length;
    if (ds.length >= 2) {
      for (let i = 0; i + 1 < ds.length; i += 2) out.thickness += Math.min(3, ds[i + 1] - ds[i]);
    } else {
      out.thickness = 0.25; // single face — assume a thin partition
    }

    const k = Math.min(1, out.count / 3) * 0.55 + Math.min(1, out.thickness / 1.5) * 0.45;
    out.lp = 21000 * Math.pow(0.035, k);          // 21 kHz → ~730 Hz fully occluded
    out.atten = 1 - 0.62 * k;
    this.stats.occluded++;
    return out;
  }

  /**
   * Allocate a spatial voice. The caller writes content into `voice.input`.
   * @returns {null | { input: GainNode, panner: PannerNode|null, send: GainNode|null,
   *                    delay: DelayNode|null, distance: number, release: (t:number)=>void }}
   */
  voice({
    position = null, velocity = null, bus = 'world', volume = 1, duration = 1,
    refDistance = 4, maxDistance = 350, rolloff = 1.1, send = 0.45,
    priority = 0.5, occlusion = true, spatial = true, propagation = true, doppler = true,
    hrtf = 'auto',
  } = {}) {
    if (!this.canPlay(priority)) { this.stats.dropped++; return null; }
    const ac = this.ac;
    const now = ac.currentTime;
    const busInput = this.buses.input(bus);
    const input = gain(ac, volume);
    const pos = position ? toVec(position, new THREE.Vector3()) : null;
    const dist = pos ? Math.max(0.05, this.listener.distanceTo(pos)) : 0;

    let head = input;
    let occ = null;
    if (spatial && pos && occlusion) {
      const o = this.occlusionAt(pos);
      if (o.lp < 20000) {
        occ = bq(ac, 'lowpass', o.lp, 0.7);
        const at = gain(ac, o.atten);
        head.connect(occ).connect(at);
        head = at;
      }
    }

    let delay = null;
    let startOffset = 0;
    if (spatial && pos && (propagation || doppler) && dist > 8) {
      const d0 = Math.min(MAX_DELAY * 0.9, dist / SPEED_OF_SOUND);
      delay = ac.createDelay(MAX_DELAY);
      delay.delayTime.setValueAtTime(d0, now);
      startOffset = d0;
      const vel = velocity ? toVec(velocity, new THREE.Vector3()) : null;
      if (doppler && vel && vel.lengthSq() > 0.25) {
        // linear ramp of delay time == constant pitch shift == Doppler
        const future = pos.clone().addScaledVector(vel, duration);
        const d1 = Math.min(MAX_DELAY * 0.9, Math.max(0.0005, this.listener.distanceTo(future) / SPEED_OF_SOUND));
        delay.delayTime.linearRampToValueAtTime(d1, now + duration);
      }
      head.connect(delay);
      head = delay;
    }

    let panner = null;
    if (spatial && pos) {
      panner = ac.createPanner();
      const useHrtf = hrtf === true || (hrtf === 'auto' && dist < 45 && this.active.length < this._hrtfBudget);
      panner.panningModel = useHrtf ? 'HRTF' : 'equalpower';
      panner.distanceModel = 'inverse';
      panner.refDistance = refDistance;
      panner.maxDistance = maxDistance;
      panner.rolloffFactor = rolloff;
      panner.coneInnerAngle = 360;
      if (panner.positionX) {
        panner.positionX.setValueAtTime(pos.x, now);
        panner.positionY.setValueAtTime(pos.y, now);
        panner.positionZ.setValueAtTime(pos.z, now);
      } else if (panner.setPosition) {
        panner.setPosition(pos.x, pos.y, pos.z);
      }
      head.connect(panner).connect(busInput);
    } else {
      head.connect(busInput);
    }

    // reverb send: distance-attenuated by hand because it bypasses the panner
    let sendGain = null;
    if (send > 0 && this.zones) {
      const dAtten = pos ? 1 / (1 + dist / Math.max(1, refDistance * 2.5)) : 1;
      sendGain = gain(ac, send * dAtten);
      head.connect(sendGain).connect(this.zones.send);
    }

    const rec = {
      input, panner, occ, delay, send: sendGain, distance: dist, startOffset,
      expires: now + startOffset + duration + 0.35,
      nodes: [input, occ, delay, panner, sendGain].filter(Boolean),
      released: false,
    };
    rec.release = (extra = 0) => { rec.expires = Math.max(rec.expires, ac.currentTime + extra); };
    this.active.push(rec);
    this.stats.spawned++;
    return rec;
  }

  /** Reap finished voices. Call once per frame. */
  update() {
    const now = this.ac.currentTime;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const v = this.active[i];
      if (v.expires <= now) {
        for (const n of v.nodes) { try { n.disconnect(); } catch { /* already gone */ } }
        this.active.splice(i, 1);
      }
    }
  }

  dispose() {
    for (const v of this.active) for (const n of v.nodes) { try { n.disconnect(); } catch { /* ignore */ } }
    this.active.length = 0;
  }
}
